import { useGetOrganizationMutation } from '@/state/mutations/organizations/use-get-organization';
import { GET_USER_QUERY_KEY, useGetUserQuery } from '@/state/queries/users/use-get-user';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { useUserStore } from '@/state/stores/use-user-store';
import type { UserDto } from '@/types/dtos/user/response/user.dto';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

function organizationIdFromPathname(pathname: string): string | undefined {
  const match = pathname.match(/\/organization\/([^/]+)/);
  return match?.[1];
}

/**
 * Keeps the client active-organization store aligned with the server user record.
 *
 * During an org switch the client updates the store and user query before the PATCH
 * settles. A slow in-flight GET of the *previous* org must not clobber that choice —
 * otherwise `/organization/:id/...` route guards see a URL/store mismatch and render
 * a blank page under the AppBar.
 */
export const SyncUserActiveOrganization = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const { setUser } = useUserStore();
  const { data: user } = useGetUserQuery();
  const setActiveOrganization = useActiveOrganizationStore((s) => s.setActiveOrganization);
  const { mutateAsync: getOrganizationMutation } = useGetOrganizationMutation();
  const fetchGenerationRef = useRef(0);

  useEffect(() => {
    if (!user) {
      return;
    }

    setUser(user);

    const serverOrgId = user.activeOrganization;
    if (!serverOrgId) {
      return;
    }

    // Read from the store at effect time (not a hook dep) so a client-ahead switch
    // does not re-schedule a fetch of the previous server org.
    const clientOrgId = useActiveOrganizationStore.getState().activeOrganization?.id;
    if (clientOrgId === serverOrgId) {
      return;
    }

    // Client + URL already agree on a different org than the server record.
    // That is the org-switch window (optimistic client, PATCH in flight or lagging
    // refetch). Do not pull the store back to the server org.
    const urlOrgId = organizationIdFromPathname(window.location.pathname);
    if (clientOrgId && urlOrgId && clientOrgId === urlOrgId) {
      return;
    }

    const generation = ++fetchGenerationRef.current;

    void (async () => {
      try {
        const newActiveOrganization = await getOrganizationMutation(serverOrgId);
        if (generation !== fetchGenerationRef.current) {
          return;
        }

        const latestServerOrgId = queryClient.getQueryData<UserDto>([GET_USER_QUERY_KEY])?.activeOrganization;
        if (!latestServerOrgId || newActiveOrganization.id !== latestServerOrgId) {
          return;
        }

        const latestClientOrgId = useActiveOrganizationStore.getState().activeOrganization?.id;
        const latestUrlOrgId = organizationIdFromPathname(window.location.pathname);
        if (latestClientOrgId && latestUrlOrgId && latestClientOrgId === latestUrlOrgId) {
          return;
        }

        setActiveOrganization(newActiveOrganization);
      } catch (error) {
        console.error('could not refetch active organization', error);
      }
    })();
  }, [user, setUser, getOrganizationMutation, setActiveOrganization, queryClient]);

  return <>{children}</>;
};
